import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberAvatar.module.scss";

/* Placeholders (q2) */
import mrImg from "@/assets/UI/memberCreate/q2Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q2MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q2Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q2MrsM.webp";

/* processed/{username}/{uuid}/images/poster.webp */
const DEFAULT_PROCESSED_SUBPATH = "images/poster.webp";

/* Framer: same “Welcome” gentle floating */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

export default function CreateMemberAvatar() {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [processedBaseKey, setProcessedBaseKey] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const title = i18n.t("onboarding.avatar.title", {
    defaultValue: "Add your avatar",
  });
  const subtitle = i18n.t("onboarding.avatar.subtitle", {
    defaultValue:
      "Upload a clear portrait. We’ll optimize it for your profile.",
  });

  const ctaUpload = i18n.t("common.upload", { defaultValue: "Upload" });
  const ctaSave = i18n.t("common.saveContinue", {
    defaultValue: "Save & Continue",
  });

  function cleanupPreview(url) {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }

  function setPickedFile(f) {
    if (!f) return;

    if (!/^image\//.test(f.type)) {
      toast.info(
        i18n.t("onboarding.avatar.onlyImages", {
          defaultValue: "Please choose an image file.",
        }),
      );
      return;
    }

    setFile(f);
    cleanupPreview(localPreview);
    const url = URL.createObjectURL(f);
    setLocalPreview(url);

    // reset upload state when user changes file
    setProcessedBaseKey("");
    setProgress(0);
  }

  function onPickFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPickedFile(f);
  }

  function onDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    setPickedFile(f);
  }

  function onDragOver(e) {
    e.preventDefault();
  }

  async function uploadOriginal() {
    if (!file) {
      toast.info(
        i18n.t("onboarding.avatar.pickFirst", {
          defaultValue: "Choose an image first.",
        }),
      );
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";

      // 1) Ask backend for presigned PUT
      const { data } = await api.post("/v1/member-create/avatar/presign", {
        ext,
        contentType: file.type || "application/octet-stream",
      });

      const { uploadUrl, processedBaseKey: baseKey } = data || {};
      if (!uploadUrl || !baseKey) throw new Error("Invalid presign response");

      setProcessedBaseKey(baseKey);

      // 2) Upload to S3
      await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });

      setProgress(100);
      toast.success(
        i18n.t("onboarding.avatar.uploaded", {
          defaultValue: "Uploaded! Converting…",
        }),
      );
    } catch (err) {
      console.error("avatar upload failed:", err);
      toast.error(
        i18n.t("onboarding.avatar.uploadError", {
          defaultValue: "Upload failed. Please try again.",
        }),
      );
      setProcessedBaseKey("");
      setProgress(0);
    } finally {
      setUploading(false);
    }
  }

  async function onFinalize(e) {
    e.preventDefault();

    if (!processedBaseKey) {
      toast.info(
        i18n.t("onboarding.avatar.missingKey", {
          defaultValue: "Upload an image first.",
        }),
      );
      return;
    }

    setFinalizing(true);
    try {
      const processedKey = `${processedBaseKey}/${DEFAULT_PROCESSED_SUBPATH}`;
      const { data } = await api.post("/v1/member-create/avatar/finalize", {
        processedKey,
      });

      toast.success(
        i18n.t("onboarding.avatar.saved", { defaultValue: "Avatar saved!" }),
      );
      navigate(data?.next || "/member/gender", { replace: true });
    } catch (err) {
      console.error("finalize avatar failed:", err);
      toast.error(
        i18n.t("onboarding.avatar.finalizeError", {
          defaultValue:
            "Couldn’t save avatar. If conversion is still running, try again in a moment.",
        }),
      );
    } finally {
      setFinalizing(false);
    }
  }

  const hasUploaded = Boolean(processedBaseKey);

  return (
    <main className={cls.page}>
      {/* Fixed background stage (Welcome-style) */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Content layer */}
      <div className={cls.pageInner}>
        <div className={cls.contentShell}>
          <div className={cls.formCard}>
            <div className={cls.formTop}>
              <div className={cls.formMeta}>
                <div className={cls.formTitle}>{title}</div>
                <div className={cls.formSub}>{subtitle}</div>
              </div>
            </div>

            <form className={cls.form} onSubmit={onFinalize} noValidate>
              {/* Dropzone */}
              <div
                className={`${cls.dropzone} ${file ? cls.hasFile : ""}`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                aria-label={i18n.t("onboarding.avatar.aria", {
                  defaultValue: "Upload avatar",
                })}
              >
                {localPreview ? (
                  <div className={cls.previewWrap}>
                    <img src={localPreview} alt="" className={cls.preview} />
                    <div className={cls.previewShade} />
                    <div className={cls.previewMeta}>
                      <div className={cls.previewTitle}>
                        {i18n.t("onboarding.avatar.selected", {
                          defaultValue: "Selected",
                        })}
                      </div>
                      <div className={cls.previewSub}>
                        {i18n.t("onboarding.avatar.tapChange", {
                          defaultValue: "Tap to change",
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={cls.dropInner}>
                    <span className={cls.dropEmoji} aria-hidden="true">
                      📷
                    </span>
                    <strong>
                      {i18n.t("onboarding.avatar.choose", {
                        defaultValue: "Choose an image",
                      })}
                    </strong>
                    <small>
                      {i18n.t("onboarding.avatar.hint", {
                        defaultValue:
                          "PNG/JPG/WebP • Clear face • Square works best",
                      })}
                    </small>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickFile}
                  className="sr-only"
                />
              </div>

              {/* Upload + progress */}
              <div className={cls.actionsRow}>
                <button
                  type="button"
                  className={cls.btn}
                  onClick={uploadOriginal}
                  disabled={!file || uploading}
                  aria-busy={uploading ? "true" : "false"}
                >
                  {uploading
                    ? i18n.t("common.uploading", { defaultValue: "Uploading…" })
                    : ctaUpload}
                </button>

                <div className={cls.progressWrap} aria-hidden={!uploading}>
                  <div className={cls.progressTrack}>
                    <div
                      className={cls.progressBar}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className={cls.progressText}>
                    {uploading ? `${progress}%` : hasUploaded ? "100%" : "0%"}
                  </div>
                </div>
              </div>

              {/* Sticky submit (always visible) */}
              <div className={cls.actions}>
                <button
                  type="submit"
                  className={cls.submit}
                  disabled={!hasUploaded || finalizing}
                  aria-busy={finalizing ? "true" : "false"}
                >
                  {finalizing
                    ? i18n.t("common.saving", { defaultValue: "Saving…" })
                    : ctaSave}
                </button>
              </div>

              {/* Helpful note */}
              <div className={cls.helpNote}>
                <span className={cls.helpDot} aria-hidden="true" />
                {i18n.t("onboarding.avatar.note", {
                  defaultValue:
                    "Tip: use a clear portrait with good lighting. We will optimize it automatically.",
                })}
              </div>
            </form>
          </div>

          <div className={cls.bottomSpace} />
        </div>
      </div>
    </main>
  );
}
